// Motywy kolorystyczne trybu skupienia. Uczeń wybiera spokojną paletę, na którą
// przełącza się panel na czas sesji. Wybór trzymany w localStorage (bez migracji).

export interface FocusThemeVars {
  bg: string; // tło strony/panelu
  surface: string; // karty (bg-white)
  surface2: string; // drugorzędne powierzchnie (bg-slate-50/100)
  border: string; // obramowania
  text: string; // tekst główny (używane w motywie ciemnym)
  muted: string; // tekst drugorzędny (motyw ciemny)
  accent: string; // kolor akcentu
  accentDark: string; // akcent hover
  accentSoft: string; // tło akcentu (np. /10)
  saturate: string; // globalne przygaszenie kolorów treści (0–1)
}

export interface FocusTheme {
  id: string;
  label: string;
  emoji: string;
  description: string;
  dark: boolean;
  vars: FocusThemeVars;
  swatch: { bg: string; accent: string };
}

export const FOCUS_THEMES: FocusTheme[] = [
  {
    id: "calm-green",
    label: "Spokojny jasny",
    emoji: "🟢",
    description: "Złamana biel i stonowana zieleń — dobry do długiej nauki",
    dark: false,
    vars: {
      bg: "#f3f6f2",
      surface: "#ffffff",
      surface2: "#eaefe8",
      border: "#dde5d9",
      text: "#233028",
      muted: "#6b7a70",
      accent: "#5b8c73",
      accentDark: "#4a7a62",
      accentSoft: "rgba(91,140,115,0.12)",
      saturate: "0.9",
    },
    swatch: { bg: "#f3f6f2", accent: "#5b8c73" },
  },
  {
    id: "cool-blue",
    label: "Chłodny",
    emoji: "🔵",
    description: "Jasny szaroniebieski — uporządkowany i spokojny",
    dark: false,
    vars: {
      bg: "#eef1f6",
      surface: "#ffffff",
      surface2: "#e5ebf3",
      border: "#d8e0ec",
      text: "#29323d",
      muted: "#6a7686",
      accent: "#5a7a9c",
      accentDark: "#4b6a8a",
      accentSoft: "rgba(90,122,156,0.12)",
      saturate: "0.9",
    },
    swatch: { bg: "#eef1f6", accent: "#5a7a9c" },
  },
  {
    id: "warm",
    label: "Ciepły",
    emoji: "🟤",
    description: "Kremowy i oliwkowy — mniej „ekranowy”, przyjemny",
    dark: false,
    vars: {
      bg: "#f6f2e9",
      surface: "#fffdf8",
      surface2: "#efe9dc",
      border: "#e6dfce",
      text: "#35301f",
      muted: "#7a7259",
      accent: "#8a875a",
      accentDark: "#767446",
      accentSoft: "rgba(138,135,90,0.14)",
      saturate: "0.92",
    },
    swatch: { bg: "#f6f2e9", accent: "#8a875a" },
  },
  {
    id: "dark",
    label: "Ciemny",
    emoji: "🌑",
    description: "Ciemny grafit i przygaszony błękit — dobry wieczorem",
    dark: true,
    vars: {
      bg: "#16191e",
      surface: "#1e222a",
      surface2: "#232833",
      border: "#2c313c",
      text: "#d7dce4",
      muted: "#8a92a0",
      accent: "#7aa2c4",
      accentDark: "#6a93b7",
      accentSoft: "rgba(122,162,196,0.16)",
      saturate: "0.95",
    },
    swatch: { bg: "#16191e", accent: "#7aa2c4" },
  },
  {
    id: "minimal",
    label: "Minimalistyczny",
    emoji: "⚪",
    description: "Jasnoszary i jeden akcent — najmniej bodźców",
    dark: false,
    vars: {
      bg: "#f2f2f3",
      surface: "#ffffff",
      surface2: "#eaeaec",
      border: "#e2e2e5",
      text: "#2b2d31",
      muted: "#767880",
      accent: "#64748b",
      accentDark: "#525c6b",
      accentSoft: "rgba(100,116,139,0.12)",
      saturate: "0.72",
    },
    swatch: { bg: "#f2f2f3", accent: "#64748b" },
  },
];

export const DEFAULT_FOCUS_THEME_ID = "calm-green";
const STORAGE_KEY = "ns_focus_theme";
export const FOCUS_THEME_EVENT = "ns:focus-theme-changed";

export function getFocusTheme(id: string | null | undefined): FocusTheme {
  return FOCUS_THEMES.find((t) => t.id === id) ?? FOCUS_THEMES[0];
}

export function getSavedFocusThemeId(): string {
  if (typeof window === "undefined") return DEFAULT_FOCUS_THEME_ID;
  try {
    return window.localStorage.getItem(STORAGE_KEY) || DEFAULT_FOCUS_THEME_ID;
  } catch {
    return DEFAULT_FOCUS_THEME_ID;
  }
}

export function saveFocusThemeId(id: string) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, id);
    window.dispatchEvent(new CustomEvent(FOCUS_THEME_EVENT, { detail: { id } }));
  } catch {
    /* ignore */
  }
}
